import React, { useState, useEffect, useRef } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Save,
  Telegram,
  Twitter,
  YouTube,
  Language
} from '@mui/icons-material';
import { adminAPI } from '../services/api';

// Вспомогательный TabPanel
const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

// Маппинг категорий на русские названия
const categoryLabels = {
  daily: 'Ежедневные',
  social: 'Социальные',
  achievements: 'Достижения',
};

// Маппинг типов верификации
const verificationLabels = {
  auto: 'Автоматическая',
  telegram_channel: 'Telegram канал',
  timer: 'Таймер (30 сек)',
  manual: 'Ручная модерация',
};

// Дефолтная форма задания
const defaultTaskForm = {
  title: '',
  description: '',
  category: 'daily',
  verification_type: 'auto',
  reward_amount: 0.1,
  reward_type: 'SPERM',
  target_url: '',
  target_username: '',
  required_count: 1,
  is_active: true,
  is_repeatable: false,
  recurrence_type: '',
  task_type: 'daily',
  task_code: '',
  task_icon: '',
};

const TasksManagement = () => {
  const [tabValue, setTabValue] = usePersistedTab('tasks', 0);
  const [tasks, setTasks] = useState([]);
  const [partnerChannels, setPartnerChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openChannelDialog, setOpenChannelDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [taskForm, setTaskForm] = useState({ ...defaultTaskForm });
  const [error, setError] = useState('');

  // Ads метрики
  const [adsStats, setAdsStats] = useState(null);
  const [adsLeaderboard, setAdsLeaderboard] = useState([]);
  const [adsSort, setAdsSort] = useState('views');
  const [adsSortDir, setAdsSortDir] = useState('desc');
  const [adsPage, setAdsPage] = useState(0);
  const [adsTotal, setAdsTotal] = useState(0);
  const [selectedAdsUser, setSelectedAdsUser] = useState(null);
  const [adsUserTransactions, setAdsUserTransactions] = useState([]);
  const [adsUserTotal, setAdsUserTotal] = useState(0);
  const [adsUserPage, setAdsUserPage] = useState(0);
  const [adsDailyView, setAdsDailyView] = useState(false);

  // Adsgram кабинет
  const [adsgramData, setAdsgramData] = useState(null);
  const [adsgramLoading, setAdsgramLoading] = useState(false);

  // Партнёрская аналитика
  const [partnerAnalytics, setPartnerAnalytics] = useState(null);

  // Аналитика заданий
  const [tasksAnalytics, setTasksAnalytics] = useState(null);
  const [tasksLeaderboard, setTasksLeaderboard] = useState([]);
  const [tasksLbSort, setTasksLbSort] = useState('earned');
  const [tasksLbPage, setTasksLbPage] = useState(0);
  const [tasksLbTotal, setTasksLbTotal] = useState(0);
  const [selectedTaskUser, setSelectedTaskUser] = useState(null);
  const [taskUserDetails, setTaskUserDetails] = useState([]);

  // Лента событий
  const [tasksFeed, setTasksFeed] = useState([]);
  const [feedPage, setFeedPage] = useState(0);
  const [feedTotal, setFeedTotal] = useState(0);
  const [feedCategory, setFeedCategory] = useState('all');

  // Статус ботов в каналах { [channelId]: { botStatus, isAdmin, message } }
  const [channelBotStatuses, setChannelBotStatuses] = useState({});
  const [loadingBotStatuses, setLoadingBotStatuses] = useState(false);

  // Форма партнерского канала
  const [channelForm, setChannelForm] = useState({
    platform: 'telegram',
    channel_name: '',
    channel_url: '',
    channel_id: '',
    description: '',
    reward_per_subscription: 0.1,
    auto_verify: false,
    verification_method: 'manual',
  });
  const [resolveStatus, setResolveStatus] = useState(null); // { ok, message, data }
  const [botCheckStatus, setBotCheckStatus] = useState(null); // { ok, message }
  const [resolving, setResolving] = useState(false);
  const [checkingBot, setCheckingBot] = useState(false);

  // ⚡ 15.09.2026: раньше тут стреляли ВСЕ 10 загрузок сразу, независимо от
  // того, какую вкладку человек открыл. Теперь на монтировании грузим только
  // loadTasks() — он нужен всегда, потому что из него считается шапка со
  // сводкой («Всего заданий», «Активных», «Выполнений») над вкладками.
  // Остальное — лениво, при первом открытии своей вкладки.
  // ⚠️ Вкладка запоминается между заходами (usePersistedTab), поэтому эффект
  // завязан на текущий tabValue, а не на предположение «стартуем с нулевой».
  useEffect(() => {
    loadTasks();
  }, []);

  const loadedTabsRef = useRef(new Set());
  useEffect(() => {
    if (loadedTabsRef.current.has(tabValue)) return;
    loadedTabsRef.current.add(tabValue);
    switch (tabValue) {
      case 0: // Ads Метрики
        loadAdsStats();
        loadAdsLeaderboard();
        loadAdsgramStats();
        break;
      case 1: // Аналитика
        loadTasksAnalytics();
        break;
      case 2: // Лента событий
        loadTasksFeed();
        break;
      case 3: // Топ заданий
        loadTasksLeaderboard();
        break;
      case 4: // Партнерские каналы
        loadPartnerChannels();
        loadPartnerAnalytics();
        loadChannelBotStatuses();
        break;
      default: // 5 «Все задания» — данные уже пришли из loadTasks() выше
        break;
    }
  }, [tabValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Автообновление ленты событий каждые 10 секунд когда вкладка активна
  useEffect(() => {
    if (tabValue !== 2) return; // Только для вкладки "Лента событий"
    const interval = setInterval(() => {
      loadTasksFeed();
    }, 10000);
    return () => clearInterval(interval);
  }, [tabValue, feedCategory, feedPage]);

  // Автообновление Ads метрик каждые 30 секунд для онлайн-статуса
  useEffect(() => {
    if (tabValue !== 0) return;
    const interval = setInterval(() => {
      loadAdsStats();
      loadAdsLeaderboard(adsSort, adsPage, adsSortDir);
    }, 30000);
    return () => clearInterval(interval);
  }, [tabValue, adsSort, adsPage, adsSortDir]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTasks();
      if (response.data.success) {
        setTasks(response.data.data || []);
      }
    } catch (err) {
      console.error('Ошибка загрузки заданий:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerChannels = async () => {
    try {
      const response = await adminAPI.getPartnerChannels();
      if (response.data.success) {
        setPartnerChannels(response.data.data || []);
      }
    } catch (err) {
      console.error('Ошибка загрузки партнерских каналов:', err);
    }
  };

  const loadChannelBotStatuses = async () => {
    try {
      setLoadingBotStatuses(true);
      const response = await adminAPI.checkAllChannelBots();
      if (response.data.success) {
        const map = {};
        for (const item of response.data.data) {
          map[item.channelId] = item;
        }
        setChannelBotStatuses(map);
      }
    } catch (err) {
      console.error('Ошибка проверки ботов в каналах:', err);
    } finally {
      setLoadingBotStatuses(false);
    }
  };

  const loadAdsStats = async () => {
    try {
      const response = await adminAPI.getAdsStats();
      if (response.data.success) {
        setAdsStats(response.data.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки ads stats:', err);
    }
  };

  const loadAdsgramStats = async () => {
    try {
      setAdsgramLoading(true);
      const response = await adminAPI.getAdsgramStats();
      if (response.data.success) {
        setAdsgramData(response.data.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки Adsgram:', err);
    } finally {
      setAdsgramLoading(false);
    }
  };

  const loadAdsLeaderboard = async (sort = 'views', page = 0, sortDir = 'desc') => {
    try {
      const response = await adminAPI.getAdsLeaderboard({ sort, sort_dir: sortDir, limit: 20, offset: page * 20 });
      if (response.data.success) {
        setAdsLeaderboard(response.data.data || []);
        setAdsTotal(response.data.total || 0);
      }
    } catch (err) {
      console.error('Ошибка загрузки ads leaderboard:', err);
    }
  };

  // Обработчик клика по заголовку для сортировки
  const handleAdsSort = (column) => {
    const newDir = (adsSort === column && adsSortDir === 'desc') ? 'asc' : 'desc';
    setAdsSort(column);
    setAdsSortDir(newDir);
    setAdsPage(0);
    loadAdsLeaderboard(column, 0, newDir);
  };

  // Стрелка сортировки для заголовка
  const adsSortArrow = (column) => {
    if (adsSort !== column) return '';
    return adsSortDir === 'desc' ? ' ▼' : ' ▲';
  };

  const loadAdsUserTransactions = async (userId, page = 0) => {
    try {
      const response = await adminAPI.getAdsUserTransactions(userId, { limit: 20, offset: page * 20 });
      if (response.data.success) {
        setAdsUserTransactions(response.data.data || []);
        setAdsUserTotal(response.data.total || 0);
        setSelectedAdsUser(userId);
        setAdsUserPage(page);
      }
    } catch (err) {
      console.error('Ошибка загрузки user ads:', err);
    }
  };

  const loadPartnerAnalytics = async () => {
    try {
      const response = await adminAPI.getPartnerAnalytics();
      if (response.data.success) {
        setPartnerAnalytics(response.data.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки партнёрской аналитики:', err);
    }
  };

  // === Аналитика заданий ===
  const loadTasksAnalytics = async () => {
    try {
      const response = await adminAPI.getTasksAnalytics();
      if (response.data.success) {
        setTasksAnalytics(response.data.data);
      }
    } catch (err) {
      console.error('Ошибка загрузки аналитики заданий:', err);
    }
  };

  const loadTasksLeaderboard = async (sort, page) => {
    try {
      const s = sort || tasksLbSort;
      const p = page !== undefined ? page : tasksLbPage;
      const response = await adminAPI.getTasksLeaderboard({ sort: s, limit: 20, offset: p * 20 });
      if (response.data.success) {
        setTasksLeaderboard(response.data.data || []);
        setTasksLbTotal(response.data.total || 0);
        setTasksLbSort(s);
        setTasksLbPage(p);
      }
    } catch (err) {
      console.error('Ошибка загрузки лидерборда заданий:', err);
    }
  };

  const loadTasksFeed = async (cat, page) => {
    try {
      const c = cat || feedCategory;
      const p = page !== undefined ? page : feedPage;
      const params = { limit: 20, offset: p * 20 };
      if (c !== 'all') params.category = c;
      const response = await adminAPI.getTasksFeed(params);
      if (response.data.success) {
        setTasksFeed(response.data.data || []);
        setFeedTotal(response.data.total || 0);
        setFeedPage(p);
        setFeedCategory(c);
      }
    } catch (err) {
      console.error('Ошибка загрузки ленты заданий:', err);
    }
  };

  const loadTaskUserDetails = async (userId) => {
    try {
      const response = await adminAPI.getTasksUserDetails(userId);
      if (response.data.success) {
        setTaskUserDetails(response.data.data || []);
        setSelectedTaskUser(userId);
      }
    } catch (err) {
      console.error('Ошибка загрузки деталей юзера:', err);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '—';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'только что';
    if (diffMin < 60) return `${diffMin} мин назад`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} ч назад`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD} дн назад`;
    return date.toLocaleDateString('ru-RU');
  };

  // Открытие диалога создания/редактирования задания
  const handleOpenDialog = (task = null) => {
    setError('');
    if (task) {
      setSelectedTask(task);
      setTaskForm({
        title: task.title || task.task_name || '',
        description: task.description || task.task_description || '',
        category: task.category || 'daily',
        verification_type: task.verification_type || 'auto',
        reward_amount: task.reward_amount || 0.1,
        reward_type: task.reward_type || 'SPERM',
        target_url: task.target_url || '',
        target_username: task.target_username || '',
        required_count: task.required_count || 1,
        is_active: task.is_active !== false,
        is_repeatable: task.is_repeatable || false,
        recurrence_type: task.recurrence_type || '',
        task_type: task.task_type || task.category || 'daily',
        task_code: task.task_code || '',
        task_icon: task.task_icon || '',
      });
    } else {
      setSelectedTask(null);
      setTaskForm({ ...defaultTaskForm });
    }
    setOpenDialog(true);
  };

  // Сохранение задания
  const handleSaveTask = async () => {
    if (!taskForm.task_code) {
      setError('Код задания (task_code) обязателен');
      return;
    }
    if (!taskForm.title) {
      setError('Название задания обязательно');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const dataToSend = { ...taskForm };
      // Синхронизируем task_type с category
      dataToSend.task_type = dataToSend.category;

      const response = selectedTask
        ? await adminAPI.updateTask(selectedTask.id, dataToSend)
        : await adminAPI.createTask(dataToSend);

      if (response.data.success) {
        loadTasks();
        setOpenDialog(false);
      } else {
        setError(response.data.error || 'Ошибка сохранения');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError(`Ошибка: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // Удаление задания
  const handleDeleteTask = async (id) => {
    if (window.confirm('Удалить это задание? Все прогресс пользователей по нему будет потерян.')) {
      try {
        const response = await adminAPI.deleteTask(id);
        if (response.data.success) {
          loadTasks();
        }
      } catch (err) {
        console.error('Ошибка удаления:', err);
      }
    }
  };

  // Быстрое переключение is_active
  const handleToggleActive = async (task) => {
    try {
      await adminAPI.updateTask(task.id, { is_active: !task.is_active });
      loadTasks();
    } catch (err) {
      console.error('Ошибка обновления:', err);
    }
  };

  // Дублирование задания
  const handleDuplicate = (task) => {
    setSelectedTask(null);
    setError('');
    setTaskForm({
      title: `${task.title || task.task_name} (копия)`,
      description: task.description || task.task_description || '',
      category: task.category || 'daily',
      verification_type: task.verification_type || 'auto',
      reward_amount: task.reward_amount || 0.1,
      reward_type: task.reward_type || 'SPERM',
      target_url: task.target_url || '',
      target_username: task.target_username || '',
      required_count: task.required_count || 1,
      is_active: true,
      is_repeatable: task.is_repeatable || false,
      recurrence_type: task.recurrence_type || '',
      task_type: task.task_type || task.category || 'daily',
      task_code: `${task.task_code}_copy`,
      task_icon: task.task_icon || '',
    });
    setOpenDialog(true);
  };

  // Партнерские каналы
  const handleOpenChannelDialog = (channel = null) => {
    setResolveStatus(null);
    setBotCheckStatus(null);
    if (channel) {
      setSelectedChannel(channel);
      setChannelForm({
        platform: channel.platform || 'telegram',
        channel_name: channel.channel_name || '',
        channel_url: channel.channel_url || '',
        channel_id: channel.channel_id || '',
        description: channel.description || '',
        reward_per_subscription: channel.reward_per_subscription || 0.1,
        auto_verify: channel.auto_verify || false,
        verification_method: channel.verification_method || 'manual',
      });
    } else {
      setSelectedChannel(null);
      setChannelForm({
        platform: 'telegram',
        channel_name: '',
        channel_url: '',
        channel_id: '',
        description: '',
        reward_per_subscription: 0.1,
        auto_verify: false,
        verification_method: 'manual',
      });
    }
    setOpenChannelDialog(true);
  };

  const handleSaveChannel = async () => {
    if (!channelForm.channel_name || !channelForm.channel_url) {
      alert('Заполните название и URL канала');
      return;
    }
    try {
      setLoading(true);
      const response = selectedChannel
        ? await adminAPI.updatePartnerChannel(selectedChannel.id, channelForm)
        : await adminAPI.createPartnerChannel(channelForm);

      if (response.data.success) {
        loadPartnerChannels();
        setOpenChannelDialog(false);
        setResolveStatus(null);
        setBotCheckStatus(null);
        alert(selectedChannel ? 'Канал обновлён!' : 'Канал добавлен!');
      } else {
        alert('Ошибка: ' + (response.data.error || 'Неизвестная ошибка'));
      }
    } catch (err) {
      console.error('Ошибка сохранения канала:', err);
      alert('Ошибка сохранения: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация
  const filteredTasks = tasks.filter(task => {
    if (filter !== 'all' && task.category !== filter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const title = (task.title || task.task_name || '').toLowerCase();
      const code = (task.task_code || '').toLowerCase();
      if (!title.includes(q) && !code.includes(q)) return false;
    }
    return true;
  });

  // Статистика
  const stats = {
    total: tasks.length,
    active: tasks.filter(t => t.is_active).length,
    daily: tasks.filter(t => t.category === 'daily').length,
    social: tasks.filter(t => t.category === 'social').length,
    achievements: tasks.filter(t => t.category === 'achievements').length,
    totalCompletions: tasks.reduce((sum, t) => sum + Number(t.total_completions || 0), 0),
    usersCompleted: tasks.reduce((sum, t) => sum + Number(t.users_completed || 0), 0),
  };

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Управление заданиями
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#6366f1' }}
        >
          Создать задание
        </Button>
      </Box>

      {/* Карточки статистики */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Всего заданий</Typography>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="body2" color="success.main">Активных: {stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Выполнений</Typography>
              <Typography variant="h4">{stats.totalCompletions}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>По категориям</Typography>
              <Typography variant="body2">Ежедневные: {stats.daily}</Typography>
              <Typography variant="body2">Социальные: {stats.social}</Typography>
              <Typography variant="body2">Достижения: {stats.achievements}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Партнерские каналы</Typography>
              <Typography variant="h4">{partnerChannels.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Табы */}
      <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="Ads Метрики" />
          <Tab label="Аналитика" />
          <Tab label="Лента событий" />
          <Tab label="Топ заданий" />
          <Tab label="Партнерские каналы" />
          <Tab label="Все задания" />
        </Tabs>

        {/* ===== Вкладка 1: АНАЛИТИКА ===== */}
        <TabPanel value={tabValue} index={1}>
          {tasksAnalytics && (
            <>
              {/* Карточки-метрики */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" gutterBottom>Юзеров выполняли</Typography>
                    <Typography variant="h4">{tasksAnalytics.overall.total_users}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" gutterBottom>Заданий выполнено</Typography>
                    <Typography variant="h4">{tasksAnalytics.overall.total_completions}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" gutterBottom>Выплачено SPERM</Typography>
                    <Typography variant="h4">{tasksAnalytics.overall.total_rewards.toFixed(2)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Card><CardContent>
                    <Typography color="text.secondary" gutterBottom>Среднее на юзера</Typography>
                    <Typography variant="h4">{tasksAnalytics.overall.avg_per_user.toFixed(2)}</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>

              {/* По категориям */}
              <Typography variant="h6" sx={{ mb: 1 }}>По категориям</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Категория</TableCell>
                      <TableCell align="right">Выполнений</TableCell>
                      <TableCell align="right">Уникальных юзеров</TableCell>
                      <TableCell align="right">Выплачено SPERM</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasksAnalytics.categories.map(cat => (
                      <TableRow key={cat.category}>
                        <TableCell><Chip label={categoryLabels[cat.category] || cat.category} size="small" /></TableCell>
                        <TableCell align="right">{cat.completions}</TableCell>
                        <TableCell align="right">{cat.unique_users}</TableCell>
                        <TableCell align="right">{cat.total_paid.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

            </>
          )}

          {/* Лидерборд юзеров */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Typography variant="h6">Топ юзеров по заработку</Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Сортировка</InputLabel>
              <Select value={tasksLbSort} label="Сортировка" onChange={(e) => { setTasksLbSort(e.target.value); loadTasksLeaderboard(e.target.value, 0); }}>
                <MenuItem value="earned">По заработку</MenuItem>
                <MenuItem value="completed">По кол-ву заданий</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell align="right">Заданий</TableCell>
                  <TableCell align="right">Заработано SPERM</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasksLeaderboard.map((u, i) => (
                  <TableRow key={u.user_id}>
                    <TableCell>{tasksLbPage * 20 + i + 1}</TableCell>
                    <TableCell>{u.user_id}</TableCell>
                    <TableCell>{u.username || u.first_name || '—'}</TableCell>
                    <TableCell>{u.telegram_id}</TableCell>
                    <TableCell align="right">{u.tasks_completed}</TableCell>
                    <TableCell align="right"><strong>{u.total_earned.toFixed(2)}</strong></TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => loadTaskUserDetails(u.user_id)}>Детали</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {tasksLbTotal > 20 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button disabled={tasksLbPage === 0} onClick={() => loadTasksLeaderboard(tasksLbSort, tasksLbPage - 1)}>Назад</Button>
              <Typography sx={{ lineHeight: '36px' }}>{tasksLbPage + 1} / {Math.ceil(tasksLbTotal / 20)}</Typography>
              <Button disabled={(tasksLbPage + 1) * 20 >= tasksLbTotal} onClick={() => loadTasksLeaderboard(tasksLbSort, tasksLbPage + 1)}>Далее</Button>
            </Box>
          )}
        </TabPanel>

        {/* ===== Вкладка 2: ЛЕНТА СОБЫТИЙ ===== */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">Лента выполнения заданий</Typography>
              <Chip label="LIVE" size="small" color="success" sx={{ animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button size="small" variant="outlined" onClick={() => loadTasksFeed()}>Обновить</Button>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Категория</InputLabel>
                <Select value={feedCategory} label="Категория" onChange={(e) => { setFeedCategory(e.target.value); loadTasksFeed(e.target.value, 0); }}>
                  <MenuItem value="all">Все</MenuItem>
                  <MenuItem value="daily">Ежедневные</MenuItem>
                  <MenuItem value="social">Социальные</MenuItem>
                  <MenuItem value="achievements">Достижения</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Время</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Задание</TableCell>
                  <TableCell>Категория</TableCell>
                  <TableCell align="right">Награда SPERM</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasksFeed.map(event => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <Tooltip title={event.completed_at ? new Date(event.completed_at).toLocaleString('ru-RU') : '—'}>
                        <span>{formatTimeAgo(event.completed_at)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{event.user_id}</TableCell>
                    <TableCell>{event.username || event.first_name || '—'}</TableCell>
                    <TableCell>{event.title} <Typography variant="caption" color="text.secondary">({event.task_code})</Typography></TableCell>
                    <TableCell><Chip label={categoryLabels[event.category] || event.category} size="small" /></TableCell>
                    <TableCell align="right">{event.reward_amount.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {tasksFeed.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center">Нет данных</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {feedTotal > 20 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button disabled={feedPage === 0} onClick={() => loadTasksFeed(feedCategory, feedPage - 1)}>Назад</Button>
              <Typography sx={{ lineHeight: '36px' }}>{feedPage + 1} / {Math.ceil(feedTotal / 20)}</Typography>
              <Button disabled={(feedPage + 1) * 20 >= feedTotal} onClick={() => loadTasksFeed(feedCategory, feedPage + 1)}>Далее</Button>
            </Box>
          )}
        </TabPanel>

        {/* ===== Вкладка 3: ТОП ЗАДАНИЙ ===== */}
        <TabPanel value={tabValue} index={3}>
          {tasksAnalytics && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>Топ заданий по выполнениям</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Название</TableCell>
                      <TableCell>Код</TableCell>
                      <TableCell>Категория</TableCell>
                      <TableCell align="right">Награда</TableCell>
                      <TableCell align="right">Выполнений</TableCell>
                      <TableCell align="right">Юзеров</TableCell>
                      <TableCell align="right">Выплачено</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasksAnalytics.top_tasks.map((t, i) => (
                      <TableRow key={t.id}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{t.title}</TableCell>
                        <TableCell><code>{t.task_code}</code></TableCell>
                        <TableCell><Chip label={categoryLabels[t.category] || t.category} size="small" /></TableCell>
                        <TableCell align="right">{t.reward_amount}</TableCell>
                        <TableCell align="right">{t.completions}</TableCell>
                        <TableCell align="right">{t.unique_users}</TableCell>
                        <TableCell align="right">{t.total_paid.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>

        {/* Вкладка: Все задания */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              placeholder="Поиск по названию или коду..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1 }}
              size="small"
            />
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Категория</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Категория"
              >
                <MenuItem value="all">Все категории</MenuItem>
                <MenuItem value="daily">Ежедневные</MenuItem>
                <MenuItem value="social">Социальные</MenuItem>
                <MenuItem value="achievements">Достижения</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Код</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Категория</TableCell>
                  <TableCell>Верификация</TableCell>
                  <TableCell>Награда</TableCell>
                  <TableCell>Выполнений</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.id}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#6366f1' }}>
                        {task.task_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {task.title || task.task_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.description || task.task_description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={categoryLabels[task.category] || task.category}
                        size="small"
                        color={
                          task.category === 'daily' ? 'primary' :
                          task.category === 'social' ? 'secondary' :
                          task.category === 'achievements' ? 'warning' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={verificationLabels[task.verification_type] || task.verification_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {Number(task.reward_amount).toFixed(1)} SPERM
                      </Typography>
                    </TableCell>
                    <TableCell>{Number(task.total_completions || 0)}</TableCell>
                    <TableCell>
                      <Chip
                        label={task.is_active ? 'Активно' : 'Выкл'}
                        size="small"
                        color={task.is_active ? 'success' : 'default'}
                        onClick={() => handleToggleActive(task)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Редактировать">
                          <IconButton size="small" onClick={() => handleOpenDialog(task)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Дублировать">
                          <IconButton size="small" onClick={() => handleDuplicate(task)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => handleDeleteTask(task.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {loading ? 'Загрузка...' : 'Заданий не найдено'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Вкладка: Партнерские каналы */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
            <Typography variant="h6">Партнерские каналы</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={loadingBotStatuses}
                onClick={loadChannelBotStatuses}
                sx={{ borderColor: '#6366f1', color: '#6366f1' }}
              >
                {loadingBotStatuses ? 'Проверка...' : 'Проверить ботов'}
              </Button>
              <Button variant="outlined" startIcon={<Add />} onClick={() => handleOpenChannelDialog()}>
                Добавить канал
              </Button>
            </Box>
          </Box>

          {/* Общая аналитика партнёрских каналов */}
          {partnerAnalytics && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={2}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="caption">Всего подписок</Typography>
                    <Typography variant="h5">{partnerAnalytics.overall?.total_rewards || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="caption">Подтверждено</Typography>
                    <Typography variant="h5" color="success.main">{partnerAnalytics.overall?.approved || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="caption">Ожидают 24ч</Typography>
                    <Typography variant="h5" color="warning.main">{partnerAnalytics.overall?.pending || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="caption">Отклонено</Typography>
                    <Typography variant="h5" color="error.main">{partnerAnalytics.overall?.rejected || 0}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="caption">Выплачено</Typography>
                    <Typography variant="h5">{Number(partnerAnalytics.overall?.total_paid || 0).toFixed(2)}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Card><CardContent>
                    <Typography color="text.secondary" variant="caption">Уник. юзеров</Typography>
                    <Typography variant="h5">{partnerAnalytics.overall?.unique_users || 0}</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>

              {/* Аналитика по каждому каналу */}
              {partnerAnalytics.channels?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Статистика по каналам</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Канал</TableCell>
                          <TableCell align="right">Клики</TableCell>
                          <TableCell align="right">Ожидают</TableCell>
                          <TableCell align="right">Подтв.</TableCell>
                          <TableCell align="right">Откл.</TableCell>
                          <TableCell align="right">Выплачено</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {partnerAnalytics.channels.map((ch) => (
                          <TableRow key={ch.id}>
                            <TableCell>{ch.channel_name}</TableCell>
                            <TableCell align="right">{ch.total_clicks}</TableCell>
                            <TableCell align="right">{ch.total_pending}</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>{ch.total_approved}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>{ch.total_rejected}</TableCell>
                            <TableCell align="right">{Number(ch.total_paid).toFixed(2)} SPERM</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />
            </Box>
          )}

          {partnerChannels.length === 0 ? (
            <Alert severity="info">
              Партнерские каналы пока не добавлены. Таблица partner_channels будет создана при первом добавлении.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {partnerChannels.map((channel) => (
                <Grid item xs={12} md={6} lg={4} key={channel.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6">{channel.channel_name}</Typography>
                        <Chip label={channel.platform} size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {channel.description || 'Без описания'}
                      </Typography>
                      <Typography variant="body2"><strong>URL:</strong> {channel.channel_url}</Typography>
                      <Typography variant="body2">
                        <strong>Награда:</strong> {channel.reward_per_subscription} SPERM
                      </Typography>
                      <Typography variant="body2">
                        <strong>Подписок:</strong> {channel.total_subscriptions || 0}
                      </Typography>
                      {channel.channel_id && (
                        <Typography variant="body2"><strong>ID:</strong> {channel.channel_id}</Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={channel.is_active ? 'Активен' : 'Неактивен'}
                          size="small"
                          color={channel.is_active ? 'success' : 'default'}
                        />
                        {channel.platform === 'telegram' && channelBotStatuses[channel.id] && (
                          channelBotStatuses[channel.id].isAdmin ? (
                            <Chip label="Бот — админ" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Бот НЕ в канале" size="small" color="error" />
                          )
                        )}
                        {channel.platform === 'telegram' && loadingBotStatuses && !channelBotStatuses[channel.id] && (
                          <Chip label="Проверка бота..." size="small" variant="outlined" />
                        )}
                      </Box>
                      {channel.platform === 'telegram' && channelBotStatuses[channel.id] && !channelBotStatuses[channel.id].isAdmin && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { py: 0.5 } }}>
                          Бот не является админом — все проверки подписок автоматически одобряются! Добавьте @moonraid_game_bot как админа канала.
                        </Alert>
                      )}
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" onClick={() => handleOpenChannelDialog(channel)}>
                          Редактировать
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={async () => {
                            if (window.confirm(`Удалить канал "${channel.channel_name}"?`)) {
                              try {
                                await adminAPI.deletePartnerChannel(channel.id);
                                loadPartnerChannels();
                              } catch (e) {
                                alert('Ошибка удаления: ' + e.message);
                              }
                            }
                          }}
                        >
                          Удалить
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* ===== Вкладка 0: ADS МЕТРИКИ ===== */}
        <TabPanel value={tabValue} index={0}>

          {/* ========== СЕКЦИЯ: ADSGRAM КАБИНЕТ ========== */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Adsgram — Кабинет партнёра</Typography>
              <Button size="small" variant="outlined" onClick={loadAdsgramStats} disabled={adsgramLoading}>
                {adsgramLoading ? 'Загрузка...' : 'Обновить'}
              </Button>
            </Box>

            {adsgramData ? (
              <>
                {/* Карточки Adsgram */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography color="text.secondary" variant="body2">Заработано</Typography>
                        <Typography variant="h5">${adsgramData.block.earned.toFixed(2)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography color="text.secondary" variant="body2">Баланс</Typography>
                        <Typography variant="h5">${adsgramData.account.balance.toFixed(2)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography color="text.secondary" variant="body2">Показов</Typography>
                        <Typography variant="h5">{adsgramData.block.impressions}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography color="text.secondary" variant="body2">Кликов</Typography>
                        <Typography variant="h5">{adsgramData.block.clicks}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography color="text.secondary" variant="body2">CPM</Typography>
                        <Typography variant="h5">${adsgramData.byDate.length > 0 ? adsgramData.byDate[0].cpm.toFixed(2) : '—'}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <Card>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography color="text.secondary" variant="body2">Fill Rate</Typography>
                        <Typography variant="h5">{adsgramData.byDate.length > 0 ? adsgramData.byDate[0].fillRate + '%' : '—'}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* По дням и По странам — рядом */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>По дням (30 дней)</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Дата</TableCell>
                            <TableCell align="right">Показов</TableCell>
                            <TableCell align="right">Кликов</TableCell>
                            <TableCell align="right">CTR</TableCell>
                            <TableCell align="right">CPM</TableCell>
                            <TableCell align="right">Заработано</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {adsgramData.byDate.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{d.date}</TableCell>
                              <TableCell align="right">{d.impressions}</TableCell>
                              <TableCell align="right">{d.clicks}</TableCell>
                              <TableCell align="right">{d.ctr.toFixed(1)}%</TableCell>
                              <TableCell align="right">${d.cpm.toFixed(2)}</TableCell>
                              <TableCell align="right">${d.earned.toFixed(3)}</TableCell>
                            </TableRow>
                          ))}
                          {adsgramData.byDate.length === 0 && (
                            <TableRow><TableCell colSpan={6} align="center">Нет данных</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>По странам</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Страна</TableCell>
                            <TableCell align="right">Показов</TableCell>
                            <TableCell align="right">Кликов</TableCell>
                            <TableCell align="right">CPM</TableCell>
                            <TableCell align="right">Заработано</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {adsgramData.byCountry.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{d.country}</TableCell>
                              <TableCell align="right">{d.impressions}</TableCell>
                              <TableCell align="right">{d.clicks}</TableCell>
                              <TableCell align="right">${d.cpm.toFixed(2)}</TableCell>
                              <TableCell align="right">${d.earned.toFixed(3)}</TableCell>
                            </TableRow>
                          ))}
                          {adsgramData.byCountry.length === 0 && (
                            <TableRow><TableCell colSpan={5} align="center">Нет данных</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Обновлено: {new Date(adsgramData.fetchedAt).toLocaleString('ru-RU')}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Typography color="text.secondary">{adsgramLoading ? 'Загрузка данных Adsgram...' : 'Нажмите "Обновить" для загрузки'}</Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* ========== СЕКЦИЯ: АНАЛИТИКА ПО СЕТЯМ (Adsgram vs Taddy) ========== */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Аналитика по рекламным сетям</Typography>

          {adsStats?.networks ? (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Adsgram */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ border: '1px solid rgba(33, 150, 243, 0.3)', background: 'rgba(33, 150, 243, 0.05)' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#2196f3' }}>
                        Adsgram
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography color="text.secondary" variant="body2">Показов</Typography>
                          <Typography variant="h5">{adsStats.networks.adsgram?.views || 0}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color="text.secondary" variant="body2">Выплачено</Typography>
                          <Typography variant="h5">{Number(adsStats.networks.adsgram?.paid || 0).toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color="text.secondary" variant="body2">Юзеров</Typography>
                          <Typography variant="h5">{adsStats.networks.adsgram?.uniqueViewers || 0}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                {/* Taddy */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ border: '1px solid rgba(255, 152, 0, 0.3)', background: 'rgba(255, 152, 0, 0.05)' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#ff9800' }}>
                        Taddy
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography color="text.secondary" variant="body2">Показов</Typography>
                          <Typography variant="h5">{adsStats.networks.taddy?.views || 0}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color="text.secondary" variant="body2">Выплачено</Typography>
                          <Typography variant="h5">{Number(adsStats.networks.taddy?.paid || 0).toFixed(2)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography color="text.secondary" variant="body2">Юзеров</Typography>
                          <Typography variant="h5">{adsStats.networks.taddy?.uniqueViewers || 0}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Таблица по дням с разбивкой по сетям */}
              {adsStats.dailyByNetwork && adsStats.dailyByNetwork.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>По дням (Adsgram vs Taddy)</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Дата</TableCell>
                          <TableCell align="right" sx={{ color: '#2196f3' }}>Adsgram</TableCell>
                          <TableCell align="right" sx={{ color: '#2196f3' }}>SPERM (A)</TableCell>
                          <TableCell align="right" sx={{ color: '#ff9800' }}>Taddy</TableCell>
                          <TableCell align="right" sx={{ color: '#ff9800' }}>SPERM (T)</TableCell>
                          <TableCell align="right">Всего</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adsStats.dailyByNetwork.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell>{new Date(d.date).toLocaleDateString('ru-RU')}</TableCell>
                            <TableCell align="right">{d.adsgram || 0}</TableCell>
                            <TableCell align="right">{Number(d.adsgram_paid || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{d.taddy || 0}</TableCell>
                            <TableCell align="right">{Number(d.taddy_paid || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{(d.adsgram || 0) + (d.taddy || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          ) : null}

          <Divider sx={{ my: 3 }} />

          {/* ========== СЕКЦИЯ: НАШИ ВНУТРЕННИЕ МЕТРИКИ ========== */}
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Наши метрики — выплаты юзерам</Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Всего просмотров</Typography>
                  <Typography variant="h4">{adsStats?.overall?.total_views || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Выплачено SPERM</Typography>
                  <Typography variant="h4">{Number(adsStats?.overall?.total_paid || 0).toFixed(2)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Уникальных зрителей</Typography>
                  <Typography variant="h4">{adsStats?.overall?.unique_viewers || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>Награда за просмотр</Typography>
                  <Typography variant="h4">0.01 SPERM</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ border: '1px solid rgba(76, 175, 80, 0.3)', background: 'rgba(76, 175, 80, 0.05)' }}>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
                    Онлайн сейчас
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#4caf50' }}>{adsStats?.online_viewers || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">{adsDailyView ? 'По дням' : 'Лидерборд по рекламе'}</Typography>
              <Button
                size="small"
                variant={adsDailyView ? 'contained' : 'outlined'}
                onClick={() => setAdsDailyView(!adsDailyView)}
              >
                {adsDailyView ? 'Лидерборд' : 'По дням'}
              </Button>
            </Box>
          </Box>

          {adsDailyView ? (
            /* Дневная агрегация */
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell align="right">Просмотров</TableCell>
                    <TableCell align="right">Выплачено SPERM</TableCell>
                    <TableCell align="right">Уник. юзеров</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(adsStats?.daily || []).map((day) => (
                    <TableRow key={day.date}>
                      <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                      <TableCell align="right">{day.views}</TableCell>
                      <TableCell align="right">{Number(day.paid).toFixed(4)}</TableCell>
                      <TableCell align="right">{day.unique_users}</TableCell>
                    </TableRow>
                  ))}
                  {(!adsStats?.daily || adsStats.daily.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">Нет данных</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Лидерборд с пагинацией */
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#90caf9' } }} onClick={() => handleAdsSort('user_id')}>
                        User ID{adsSortArrow('user_id')}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#90caf9' } }} onClick={() => handleAdsSort('username')}>
                        Username{adsSortArrow('username')}
                      </TableCell>
                      <TableCell>Страна</TableCell>
                      <TableCell>IP</TableCell>
                      <TableCell>Язык</TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#90caf9' } }} onClick={() => handleAdsSort('views')}>
                        Просмотров{adsSortArrow('views')}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#90caf9' } }} onClick={() => handleAdsSort('earned')}>
                        Заработано SPERM{adsSortArrow('earned')}
                      </TableCell>
                      <TableCell sx={{ cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#90caf9' } }} onClick={() => handleAdsSort('last_view')}>
                        Последний просмотр{adsSortArrow('last_view')}
                      </TableCell>
                      <TableCell>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adsLeaderboard.map((row, idx) => {
                      const isOnline = row.last_active && (Date.now() - new Date(row.last_active).getTime()) < 5 * 60 * 1000;
                      return (
                        <TableRow key={row.user_id} sx={isOnline ? { background: 'rgba(76, 175, 80, 0.08)', '&:hover': { background: 'rgba(76, 175, 80, 0.15)' } } : {}}>
                          <TableCell>{adsPage * 20 + idx + 1}</TableCell>
                          <TableCell>{row.user_id}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isOnline && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50', flexShrink: 0, animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />}
                              {row.username || row.first_name || `ID: ${row.telegram_id}`}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{row.country || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace', opacity: 0.8 }}>{row.ip_address || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{row.language_code || '—'}</TableCell>
                          <TableCell>{row.view_count}</TableCell>
                          <TableCell>{Number(row.total_earned).toFixed(4)}</TableCell>
                          <TableCell>{new Date(row.last_view).toLocaleString()}</TableCell>
                          <TableCell>
                            <Button size="small" onClick={() => { setAdsUserPage(0); loadAdsUserTransactions(row.user_id, 0); }}>
                              Детали
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {adsLeaderboard.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography color="text.secondary">Нет данных по рекламе</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {adsTotal > 20 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Button
                    size="small"
                    disabled={adsPage === 0}
                    onClick={() => { const p = adsPage - 1; setAdsPage(p); loadAdsLeaderboard(adsSort, p, adsSortDir); }}
                  >
                    Назад
                  </Button>
                  <Typography variant="body2">
                    {adsPage * 20 + 1}–{Math.min((adsPage + 1) * 20, adsTotal)} из {adsTotal}
                  </Typography>
                  <Button
                    size="small"
                    disabled={(adsPage + 1) * 20 >= adsTotal}
                    onClick={() => { const p = adsPage + 1; setAdsPage(p); loadAdsLeaderboard(adsSort, p, adsSortDir); }}
                  >
                    Далее
                  </Button>
                </Box>
              )}
            </>
          )}
        </TabPanel>
      </Paper>

      {/* Диалог транзакций рекламы юзера */}
      <Dialog open={!!selectedAdsUser} onClose={() => setSelectedAdsUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>Транзакции рекламы — User #{selectedAdsUser} ({adsUserTotal} всего)</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Сумма</TableCell>
                  <TableCell>Баланс до</TableCell>
                  <TableCell>Баланс после</TableCell>
                  <TableCell>Дата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adsUserTransactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{tx.id}</TableCell>
                    <TableCell>+{Number(tx.amount).toFixed(4)} SPERM</TableCell>
                    <TableCell>{Number(tx.balance_before).toFixed(2)}</TableCell>
                    <TableCell>{Number(tx.balance_after).toFixed(2)}</TableCell>
                    <TableCell>{new Date(tx.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {adsUserTotal > 20 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
              <Button
                size="small"
                disabled={adsUserPage === 0}
                onClick={() => loadAdsUserTransactions(selectedAdsUser, adsUserPage - 1)}
              >
                Назад
              </Button>
              <Typography variant="body2">
                {adsUserPage * 20 + 1}–{Math.min((adsUserPage + 1) * 20, adsUserTotal)} из {adsUserTotal}
              </Typography>
              <Button
                size="small"
                disabled={(adsUserPage + 1) * 20 >= adsUserTotal}
                onClick={() => loadAdsUserTransactions(selectedAdsUser, adsUserPage + 1)}
              >
                Далее
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAdsUser(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог создания/редактирования задания */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTask ? `Редактировать задание #${selectedTask.id}` : 'Создать новое задание'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Код задания (уникальный, обязательный) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Код задания (task_code) *"
                value={taskForm.task_code}
                onChange={(e) => setTaskForm({ ...taskForm, task_code: e.target.value })}
                helperText="Уникальный код, например: daily_login, tg_channel_subscribe"
                disabled={!!selectedTask}
              />
            </Grid>
            {/* Категория */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Категория</InputLabel>
                <Select
                  value={taskForm.category}
                  onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value, task_type: e.target.value })}
                  label="Категория"
                >
                  <MenuItem value="daily">Ежедневные</MenuItem>
                  <MenuItem value="social">Социальные</MenuItem>
                  <MenuItem value="achievements">Достижения</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Название */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название задания *"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
              />
            </Grid>
            {/* Описание */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                multiline
                rows={2}
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
              />
            </Grid>
            {/* Тип верификации */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Тип верификации</InputLabel>
                <Select
                  value={taskForm.verification_type}
                  onChange={(e) => setTaskForm({ ...taskForm, verification_type: e.target.value })}
                  label="Тип верификации"
                >
                  <MenuItem value="auto">Автоматическая (БД)</MenuItem>
                  <MenuItem value="telegram_channel">Telegram канал</MenuItem>
                  <MenuItem value="timer">Таймер 30 сек</MenuItem>
                  <MenuItem value="manual">Ручная модерация</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Награда */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Награда"
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                value={taskForm.reward_amount}
                onChange={(e) => setTaskForm({ ...taskForm, reward_amount: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">SPERM</InputAdornment>
                }}
              />
            </Grid>
            {/* Кол-во выполнений */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Требуемое кол-во"
                type="number"
                inputProps={{ min: 1 }}
                value={taskForm.required_count}
                onChange={(e) => setTaskForm({ ...taskForm, required_count: parseInt(e.target.value) || 1 })}
                helperText="Сколько раз нужно выполнить"
              />
            </Grid>
            {/* URL */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="URL (ссылка)"
                value={taskForm.target_url}
                onChange={(e) => setTaskForm({ ...taskForm, target_url: e.target.value })}
                helperText="Для Telegram, Twitter, YouTube"
              />
            </Grid>
            {/* Username */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username канала"
                value={taskForm.target_username}
                onChange={(e) => setTaskForm({ ...taskForm, target_username: e.target.value })}
                helperText="@username для Telegram проверки"
              />
            </Grid>
            {/* Иконка */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Иконка (эмодзи)"
                value={taskForm.task_icon}
                onChange={(e) => setTaskForm({ ...taskForm, task_icon: e.target.value })}
                placeholder="📋"
              />
            </Grid>
            {/* Переключатели */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={taskForm.is_active}
                      onChange={(e) => setTaskForm({ ...taskForm, is_active: e.target.checked })}
                    />
                  }
                  label="Активное"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={taskForm.is_repeatable}
                      onChange={(e) => setTaskForm({ ...taskForm, is_repeatable: e.target.checked })}
                    />
                  }
                  label="Повторяющееся"
                />
              </Box>
            </Grid>
            {taskForm.is_repeatable && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Тип повторения</InputLabel>
                  <Select
                    value={taskForm.recurrence_type}
                    onChange={(e) => setTaskForm({ ...taskForm, recurrence_type: e.target.value })}
                    label="Тип повторения"
                  >
                    <MenuItem value="daily">Ежедневно</MenuItem>
                    <MenuItem value="weekly">Еженедельно</MenuItem>
                    <MenuItem value="monthly">Ежемесячно</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Отмена</Button>
          <Button
            onClick={handleSaveTask}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#6366f1' }}
          >
            {selectedTask ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог партнерского канала */}
      <Dialog open={openChannelDialog} onClose={() => setOpenChannelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedChannel ? 'Редактировать канал' : 'Добавить партнерский канал'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Платформа</InputLabel>
                <Select
                  value={channelForm.platform}
                  onChange={(e) => setChannelForm({ ...channelForm, platform: e.target.value })}
                  label="Платформа"
                >
                  <MenuItem value="telegram">Telegram</MenuItem>
                  <MenuItem value="twitter">Twitter/X</MenuItem>
                  <MenuItem value="youtube">YouTube</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="URL или @username канала"
                  value={channelForm.channel_url}
                  onChange={(e) => {
                    setChannelForm({ ...channelForm, channel_url: e.target.value });
                    setResolveStatus(null);
                    setBotCheckStatus(null);
                  }}
                  placeholder="https://t.me/channel или @channel"
                />
                <Button
                  variant="contained"
                  disabled={resolving || !channelForm.channel_url}
                  onClick={async () => {
                    setResolving(true);
                    setResolveStatus(null);
                    try {
                      const resp = await adminAPI.resolveChannel(channelForm.channel_url);
                      if (resp.data.success) {
                        const d = resp.data.data;
                        setChannelForm(prev => ({
                          ...prev,
                          channel_id: d.channel_id,
                          channel_name: prev.channel_name || d.title || '',
                        }));
                        setResolveStatus({ ok: true, message: `Найден: ${d.title} (${d.members_count} подписчиков)` });
                      } else {
                        setResolveStatus({ ok: false, message: resp.data.error || 'Канал не найден' });
                      }
                    } catch (e) {
                      setResolveStatus({ ok: false, message: 'Ошибка запроса' });
                    }
                    setResolving(false);
                  }}
                  sx={{ minWidth: 120, height: 56, bgcolor: '#6366f1' }}
                >
                  {resolving ? '...' : 'Резолв'}
                </Button>
              </Box>
              {resolveStatus && (
                <Alert severity={resolveStatus.ok ? 'success' : 'error'} sx={{ mt: 1 }}>
                  {resolveStatus.message}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название канала"
                value={channelForm.channel_name}
                onChange={(e) => setChannelForm({ ...channelForm, channel_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID канала (авто)"
                value={channelForm.channel_id}
                onChange={(e) => setChannelForm({ ...channelForm, channel_id: e.target.value })}
                helperText="Заполняется автоматически при резолве"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={checkingBot || !channelForm.channel_id}
                  onClick={async () => {
                    setCheckingBot(true);
                    setBotCheckStatus(null);
                    try {
                      const resp = await adminAPI.checkBotInChannel(channelForm.channel_id);
                      if (resp.data.success) {
                        const d = resp.data.data;
                        setBotCheckStatus({ ok: d.isAdmin, message: d.message });
                      } else {
                        setBotCheckStatus({ ok: false, message: resp.data.error || 'Ошибка' });
                      }
                    } catch (e) {
                      setBotCheckStatus({ ok: false, message: 'Ошибка запроса' });
                    }
                    setCheckingBot(false);
                  }}
                  sx={{
                    height: 56,
                    borderColor: botCheckStatus ? (botCheckStatus.ok ? '#10b981' : '#ef4444') : '#6366f1',
                    color: botCheckStatus ? (botCheckStatus.ok ? '#10b981' : '#ef4444') : '#6366f1',
                  }}
                >
                  {checkingBot ? '...' : botCheckStatus ? (botCheckStatus.ok ? '✅ Бот — админ' : '❌ Бот НЕ админ') : '🤖 Проверить бота'}
                </Button>
              </Box>
              {botCheckStatus && !botCheckStatus.ok && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {botCheckStatus.message}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Награда за подписку"
                type="number"
                inputProps={{ step: 0.1, min: 0 }}
                value={channelForm.reward_per_subscription}
                onChange={(e) => setChannelForm({ ...channelForm, reward_per_subscription: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">SPERM</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Описание"
                multiline
                rows={2}
                value={channelForm.description}
                onChange={(e) => setChannelForm({ ...channelForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChannelDialog(false)}>Отмена</Button>
          <Button
            onClick={handleSaveChannel}
            variant="contained"
            disabled={loading}
            sx={{ bgcolor: '#6366f1' }}
          >
            {selectedChannel ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Модалка деталей юзера по заданиям */}
      <Dialog open={!!selectedTaskUser} onClose={() => setSelectedTaskUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>Задания юзера #{selectedTaskUser} ({taskUserDetails.length} выполнено)</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Задание</TableCell>
                  <TableCell>Код</TableCell>
                  <TableCell>Категория</TableCell>
                  <TableCell align="right">Награда</TableCell>
                  <TableCell>Дата выполнения</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {taskUserDetails.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell>{t.title}</TableCell>
                    <TableCell><code>{t.task_code}</code></TableCell>
                    <TableCell><Chip label={categoryLabels[t.category] || t.category} size="small" /></TableCell>
                    <TableCell align="right">{t.reward_amount.toFixed(2)}</TableCell>
                    <TableCell>{t.completed_at ? new Date(t.completed_at).toLocaleString('ru-RU') : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Итого заработано: <strong>{taskUserDetails.reduce((s, t) => s + t.reward_amount, 0).toFixed(2)} SPERM</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTaskUser(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TasksManagement;
